import { Injectable } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { ComponentStore, tapResponse } from '@ngrx/component-store'
import {
  Observable,
  switchMap,
  tap,
  filter,
  map,
  withLatestFrom,
  mergeMap,
} from 'rxjs'
import { omit } from 'lodash'

import {
  GoodsReceive,
  GoodsReceiveField,
  GoodsReceiveSearchParams,
  GoodsReceivesListingData,
  GoodsReceivesListingPage,
} from '../goods-receive.model'
import { isQueryStringFiltered } from '../../../libs/query-string.lib'
import { Page } from '../../../models/util.model'
import { GoodsReceivesRepository } from '../goods-receives.repository'
import { Warehouse } from '../../warehouses'
import { Supplier } from '../../suppliers'
import { Payment } from '../../payments'
import { Reason } from '../../reasons'

interface GoodsReceivesListingState extends GoodsReceivesListingPage {
  searchString?: string
  selected?: GoodsReceive[]
  fields?: GoodsReceiveField[]
  filters?: GoodsReceiveSearchParams
  searchKeys?: GoodsReceiveSearchParams
  customFilters?: GoodsReceiveSearchParams
  error?: HttpErrorResponse
  toReload: boolean
  isLoading: boolean
  isInitialized: boolean
}

export interface GoodsReceiveFilterData {
  params?: GoodsReceiveSearchParams
  count?: number
}

const GOODS_RECEIVES_INITIAL_STATE: GoodsReceivesListingState = {
  isLoading: false,
  isInitialized: false,
  toReload: false,
}

@Injectable()
export class GoodsReceivesListingUseCase extends ComponentStore<GoodsReceivesListingState> {
  constructor(private goodsReceivesRepository: GoodsReceivesRepository) {
    super(GOODS_RECEIVES_INITIAL_STATE)
  }

  // Selectors

  readonly selectState$: Observable<GoodsReceivesListingState> = this.select(
    (state) => state,
  )

  readonly selectIsInitialized$: Observable<boolean> = this.select(
    (state) => state.isInitialized,
  )

  readonly selectSearchString$: Observable<string | undefined> = this.select(
    (state) => state.searchString,
  )

  readonly selectFilters$: Observable<GoodsReceiveSearchParams | undefined> =
    this.select((state) => state.filters)

  readonly selectSearchFilters$: Observable<
    GoodsReceiveSearchParams | undefined
  > = this.selectFilters$.pipe(
    mergeMap((filters) =>
      this.selectSearchString$.pipe(
        map((q) => ({ ...(q ? { q } : {}), ...(filters ? filters : {}) })),
      ),
    ),
  )

  readonly selectFilterData$: Observable<GoodsReceiveFilterData> =
    this.selectState$.pipe(map((state) => this.parseStateFilterData(state)))

  readonly selectIsLoading$: Observable<boolean> = this.select(
    (state) => state.isLoading,
  )

  readonly selectIsErrored$: Observable<boolean> = this.select(
    (state) => !!state.error,
  )

  readonly selectIsFiltered$: Observable<boolean> =
    this.selectSearchFilters$.pipe(
      filter((filters) => !!filters),
      map((filters) => isQueryStringFiltered(filters || {})),
    )

  readonly selectToReload$: Observable<boolean> = this.select(
    (state) => state.toReload,
  )

  readonly selectIsInitialized?: Observable<boolean> = this.select(
    (state) => state.isInitialized,
  )

  readonly selectCustomFilters$: Observable<
    GoodsReceiveSearchParams | undefined
  > = this.select((state) => state.customFilters)

  readonly selectSearchKeys$ = this.select((state) => state.searchKeys)

  readonly selectGoodsReceives$: Observable<GoodsReceive[]> = this.select(
    (state) => state.data || [],
  )

  readonly selectGoodsReceivesSelected$: Observable<
    GoodsReceive[] | undefined
  > = this.select((state) => state.selected)

  readonly selectTotalCount$: Observable<number> = this.select(
    (state) => state.totalCount || 0,
  )

  readonly selectFields$: Observable<GoodsReceiveField[] | undefined> =
    this.select((state) => state.fields)

  readonly selectExtData$: Observable<GoodsReceivesListingData | undefined> =
    this.select((state) => state.extData)

  readonly selectWarehouses$: Observable<Warehouse[] | undefined> =
    this.selectExtData$.pipe(map((extData) => extData?.warehouses))

  readonly selectSuppliers$: Observable<Supplier[] | undefined> =
    this.selectExtData$.pipe(map((extData) => extData?.suppliers))

  readonly selectPayments$: Observable<Payment[] | undefined> =
    this.selectExtData$.pipe(map((extData) => extData?.payments))

  readonly selectReasons$: Observable<Reason[] | undefined> =
    this.selectExtData$.pipe(map((extData) => extData?.reasons))

  readonly selectPage$: Observable<Page<GoodsReceive>> =
    this.selectIsLoading$.pipe(
      filter((isLoading) => !isLoading),
      switchMap(() =>
        this.select(
          this.selectGoodsReceives$,
          this.selectTotalCount$,
          (data, totalCount) => ({ totalCount, data }),
          { debounce: true },
        ),
      ),
    )

  /**
   * EFFECTS
   */

  readonly search$ = this.effect(
    (searchParams$: Observable<GoodsReceiveSearchParams>) => {
      return searchParams$.pipe(
        tap((searchParams) => this.setFilters(searchParams)),
        withLatestFrom(this.selectState$),
        filter(([searchParams, state]) => !!state.fields),
        switchMap(([searchParams, state]) =>
          this.goodsReceivesRepository
            .searchGoodsReceives$(
              this.parseStateFilterParams(state),
              state.fields || [],
              state.extData,
            )
            .pipe(
              tapResponse(
                (pageData) => this.setPage(pageData),
                (error: HttpErrorResponse) => this.setError(error),
              ),
            ),
        ),
      )
    },
  )

  readonly searchAll$ = this.effect(
    (searchParams$: Observable<GoodsReceiveSearchParams>) => {
      return searchParams$.pipe(
        withLatestFrom(this.selectState$),
        filter(([params, state]) => !!state.fields),
        map(([params, state]) => ({
          params: {
            ...params,
            ...(state.searchString ? { q: state.searchString } : {}),
            limit: state.totalCount,
          },
          fields: state.fields || [],
          extData: state.extData,
        })),
        tap((searchParams) => this.setFilters(searchParams.params)),
        switchMap(({ params, fields, extData }) =>
          this.goodsReceivesRepository
            .searchGoodsReceives$(params, fields, extData)
            .pipe(
              tapResponse(
                (pageData) => this.setPage(pageData),
                (error: HttpErrorResponse) => this.setError(error),
              ),
            ),
        ),
      )
    },
  )

  /**
   * REDUCERS
   */

  readonly setIsLoading = this.updater(
    (state, isLoading: boolean): GoodsReceivesListingState => {
      return {
        ...state,
        isLoading,
      }
    },
  )

  readonly setSearchString = this.updater(
    (state, searchString: string): GoodsReceivesListingState => {
      return {
        ...state,
        searchString,
        toReload: true,
      }
    },
  )

  readonly setGoodsReceivesSelected = this.updater(
    (
      state,
      goodsReceives: GoodsReceive[] | undefined,
    ): GoodsReceivesListingState => {
      return {
        ...state,
        selected: goodsReceives,
      }
    },
  )

  readonly setPage = this.updater(
    (state, page: GoodsReceivesListingPage): GoodsReceivesListingState => {
      return {
        ...state,
        data: page.data,
        extData: page.extData,
        totalCount: page.totalCount,
        toReload: false,
        isLoading: false,
        isInitialized: true,
      }
    },
  )

  readonly setError = this.updater(
    (state, error: HttpErrorResponse): GoodsReceivesListingState => {
      return {
        ...state,
        data: undefined,
        totalCount: 0,
        error,
        isLoading: false,
        toReload: false,
      }
    },
  )

  readonly setReload = this.updater(
    (state, toReload: boolean): GoodsReceivesListingState => {
      return {
        ...state,
        toReload,
      }
    },
  )

  readonly setFields = this.updater(
    (state, fields: GoodsReceiveField[]): GoodsReceivesListingState => {
      return {
        ...state,
        fields,
        toReload: state.isInitialized,
      }
    },
  )

  readonly setFilters = this.updater(
    (state, filters?: GoodsReceiveSearchParams): GoodsReceivesListingState => {
      return {
        ...state,
        filters,
        toReload: false,
        isLoading: true,
        isInitialized: state.isInitialized,
      }
    },
  )

  readonly setCustomFilters = this.updater(
    (
      state,
      customFilters: GoodsReceiveSearchParams,
    ): GoodsReceivesListingState => {
      return {
        ...state,
        customFilters,
        toReload: state.isInitialized,
      }
    },
  )

  readonly setSearchKeys = this.updater(
    (
      state,
      searchKeys?: GoodsReceiveSearchParams,
    ): GoodsReceivesListingState => {
      return {
        ...state,
        searchKeys,
        toReload: state.isInitialized,
      }
    },
  )

  readonly resetState = this.updater(
    (state): GoodsReceivesListingState => GOODS_RECEIVES_INITIAL_STATE,
  )

  readonly resetError = this.updater((state): GoodsReceivesListingState => {
    return {
      ...state,
      error: undefined,
    }
  })

  readonly resetFilters = this.updater((state): GoodsReceivesListingState => {
    return {
      ...state,
      filters: undefined,
      searchString: undefined,
      error: undefined,
      customFilters: undefined,
      toReload: true,
    }
  })

  /**
   * Utilities
   */

  private parseStateFilterParams(
    state: GoodsReceivesListingState,
  ): GoodsReceiveSearchParams {
    let params: GoodsReceiveSearchParams = state.filters || {}
    if (state.searchString) {
      params.q = state.searchString
    }
    if (state.customFilters) {
      params = { ...params, ...state.customFilters }
    }
    if (state.searchKeys) {
      params = { ...params, ...state.searchKeys }
    }

    return params
  }

  private parseStateFilterData(
    state: GoodsReceivesListingState,
  ): GoodsReceiveFilterData {
    if (state.selected?.length) {
      return {
        params: {
          _id: state.selected.map((o) => o._id),
        },
        count: state.selected.length,
      }
    }

    return {
      params: omit(this.parseStateFilterParams(state), [
        'limit',
        'offset',
        'sort',
        'order',
      ]),
      count: state.totalCount,
    }
  }
}
